import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { DEMO_JOBS, DEMO_CAMPAIGNS, DEMO_AUDIT_LOG, daysUntil, deadlineWindow, type DeadlineWindow } from '../data/demoProxyData';

const WINDOW_STYLE: Record<DeadlineWindow, { bg: string; fg: string }> = {
  Abort:       { bg: '#FEF2F2', fg: '#991b1b' },
  'Email-only': { bg: '#EFF6FF', fg: '#1e40af' },
  POD:         { bg: '#FFFBEB', fg: '#92400e' },
  Red:         { bg: '#FFF7ED', fg: '#9a3412' },
  Yellow:      { bg: '#FEFCE8', fg: '#713f12' },
  Green:       { bg: '#F0FDF4', fg: '#166534' },
};

const OUTREACH_STATUS_LABEL: Record<string, string> = {
  ready:     'Ready to send',
  scheduled: 'Scheduled',
  blocked:   'Blocked',
};

// Build board rows from DEMO_JOBS — only jobs with campaigns
function buildBoardRows() {
  return Object.values(DEMO_JOBS)
    .map(job => {
      const days = daysUntil(job.meeting);
      const campaign = DEMO_CAMPAIGNS.find(c => c.jobId === job.jobId);
      const win = deadlineWindow(days, false);
      return { job, days, win, campaign };
    })
    .filter(r => r.days >= 0 && r.days <= 25)
    .sort((a, b) => a.days - b.days);
}

export function ProxyBoardPage() {
  const { setTopbar } = useAppShell();
  const [auditLog] = useState(DEMO_AUDIT_LOG.map(e => ({ time: e.time, text: e.text })));
  const rows = buildBoardRows();

  useEffect(() => {
    setTopbar('Deadline Board', 'One board replacing two Smartsheets — 10-day, 5-day and under-5-day windows');
  }, [setTopbar]);

  const inside10  = rows.filter(r => r.days <= 10).length;
  const openFU    = DEMO_CAMPAIGNS.filter(c => c.status === 'ready' || c.status === 'scheduled').length;
  const shortages = Object.values(DEMO_JOBS).filter(j => j.ordered && j.received && j.received < j.ordered).length;
  const exceptions = 2; // bounce + unclassified

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Rule cards */}
        <div data-tour="board-rules" className="grid grid-cols-3 gap-4 mb-6">
          {[
            { window: '10 days', color: '#92400e', bg: '#FFFBEB', border: '#FCD34D',
              title: 'Print on demand', body: 'POD allowed when the proxy statement is ≤100 pages and the order is ≤50 sets. An analyst still confirms.' },
            { window: '5 days', color: '#1e40af', bg: '#EFF6FF', border: '#BFDBFE',
              title: 'Email-only', body: 'Stop physical distribution at the 5-day mark unless sets are already in the warehouse.' },
            { window: 'Under 5 days', color: '#991b1b', bg: '#FEF2F2', border: '#FECACA',
              title: 'Candidate for abort', body: 'Ace recommends abort per ICT-8. The job stays open. A person decides.' },
          ].map(rule => (
            <div key={rule.window} className="rounded-2xl border p-4" style={{ background: rule.bg, borderColor: rule.border }}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: rule.color }}>{rule.window}</p>
              <p className="text-sm font-semibold text-ink mb-1">{rule.title}</p>
              <p className="text-xs leading-snug" style={{ color: rule.color }}>{rule.body}</p>
            </div>
          ))}
        </div>

        {/* KPI row */}
        <div data-tour="board-kpis" className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Open follow-ups',  value: openFU,    color: '#14b8a6' },
            { label: 'Inside 10 days',   value: inside10,  color: '#92400e' },
            { label: 'Shortages open',   value: shortages, color: '#b91c1c' },
            { label: 'Exceptions',       value: exceptions, color: '#7c3aed' },
          ].map(k => (
            <div key={k.label} className="rounded-2xl border border-trim p-4 bg-white">
              <p className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</p>
              <p className="text-xs text-muted mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-6 items-start">
          {/* Job table */}
          <div data-tour="board-jobs" className="flex-1 rounded-2xl border border-trim overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-trim bg-[#F8FAFC]">
                  <th className="text-left px-4 py-3 font-semibold text-muted">Job</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted">Issuer</th>
                  <th className="text-right px-4 py-3 font-semibold text-muted">Days</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted">Window</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted">Outreach</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ job, days, win, campaign }) => {
                  const ws = WINDOW_STYLE[win];
                  const cs = campaign?.status;
                  return (
                    <tr key={job.jobId} className="border-b border-trim last:border-0 hover:bg-[#F8FAFC] transition-colors">
                      <td className="px-4 py-2.5 font-mono text-muted">{job.jobId}</td>
                      <td className="px-4 py-2.5 font-medium text-ink">{job.issuer}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold"
                        style={{ color: days <= 5 ? '#991b1b' : days <= 10 ? '#92400e' : '#166534' }}>
                        {days}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: ws.bg, color: ws.fg }}>
                          {win}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {cs ? (
                          <Link to="/proxy/outreach" className="text-[10px] font-semibold no-underline"
                            style={{ color: cs === 'blocked' ? '#991b1b' : cs === 'scheduled' ? '#92400e' : '#0f6e56' }}>
                            {OUTREACH_STATUS_LABEL[cs]}
                          </Link>
                        ) : (
                          <span className="text-[10px] text-muted">Material on file</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Session audit */}
          <div data-tour="board-audit" className="w-72 flex-shrink-0 rounded-2xl border border-trim overflow-hidden">
            <div className="px-4 py-3 border-b border-trim bg-[#F8FAFC]">
              <p className="text-xs font-semibold text-ink">Session audit</p>
            </div>
            <div className="divide-y divide-trim">
              {auditLog.map((entry, i) => (
                <div key={i} className="px-4 py-3">
                  <p className="text-[10px] font-mono text-muted mb-0.5">{entry.time}</p>
                  <p className="text-xs text-ink leading-snug">{entry.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
